/**
 * **The folders live in the left menu, under Documents** (W25, FR-60, plan D76).
 *
 * The tree is the package's (`useProDocFolderMenu`): *Every workspace*, *Shared
 * with me*, each workspace this person sees with its folders at any depth, and
 * the same "…" actions by permission. This provider is the host's half of it:
 *
 * - **The address is the place.** Choosing one opens Documents with it in the
 *   query string — the register's own keys (`workspace`, `folder`, `sub`,
 *   `shared`, via `libraryQueryToParams`) — as a **new step in the history**, so
 *   back and forward walk the places chosen. On Documents every other filter is
 *   kept; from any other screen a choice starts from the register's defaults.
 *   The place in the address is what the tree highlights.
 * - **The tree's state is here**, not in the panel: while the drawer is open
 *   the shell mounts the panel twice (the drawer, and the rail it covers) and it
 *   unmounts it while the group is closed (`NavItem.panel`, fiveox #446).
 * - **The dialogs mount once**, beside the shell — new folder, rename, move,
 *   archive and share, the share with this app's members, step-up and link
 *   origin (`useSharingHost`). After one changes something the register reads
 *   its folders again (`useFolderMenu().revision`, its `folderRevision`).
 *
 * `App.tsx` renders it inside `OrgProvider` and `StepUpProvider` and around
 * `<PlatformShell>`, whose Documents item takes `panel`; the drawer closes on a
 * choice because the shell's `drawerKey` is the address's query string.
 */
import { libraryQueryFromParams, libraryQueryToParams, useProDocFolderMenu, type ProDocFolderMenu } from '@fiveox/prodoc-ui'
import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router'

import { SERVICE_API } from '../lib/config'
import { session } from '../lib/session'
import { useOrg } from './org-context'
import { useSharingHost } from './sharing-host'

/** The register — the one screen whose address is a place. */
export const REGISTER_PATH = '/documents'

const FolderMenuContext = createContext<ProDocFolderMenu | null>(null)

export function FolderMenuProvider({ children }: { children: (menu: ProDocFolderMenu) => ReactNode }) {
  const { org, userId } = useOrg()
  const sharing = useSharingHost()
  const { pathname, search } = useLocation()
  const navigate = useNavigate()
  const onRegister = pathname === REGISTER_PATH
  const query = useMemo(() => (onRegister ? libraryQueryFromParams(new URLSearchParams(search)) : null), [onRegister, search])

  const menu = useProDocFolderMenu({
    apiUrl: SERVICE_API,
    fetcher: session.authed,
    orgId: org.id,
    userId,
    ...sharing,
    query,
    onChoose: (next) => {
      const params = libraryQueryToParams(next).toString()
      // A new step in the history, so back and forward walk the places; choosing where you stand again is not one.
      const same = onRegister && params === search.replace(/^\?/, '')
      navigate(`${REGISTER_PATH}${params ? `?${params}` : ''}`, { replace: same })
    },
  })

  return (
    <FolderMenuContext.Provider value={menu}>
      {children(menu)}
      {menu.dialogs}
    </FolderMenuContext.Provider>
  )
}

/** The menu's tree as the register sees it — its `revision` and `refresh`. `null` outside `<FolderMenuProvider>`. */
export function useFolderMenu(): ProDocFolderMenu | null {
  return useContext(FolderMenuContext)
}
